import { Alerts } from "../../../../utils/Alerts.js";
import { RPCs } from "../../../../services/rpc.js";
import { useRPCContext } from "../../../../contexts/RPCContext.js";
import { useRPC } from "../../../../hooks/useRPC/index.js";
import { MailAction } from "../../components/MailMessageForm/index.js";
import { useMailMessage } from "../../hooks/useMailMessage.js";
import { useEffect, useMemo, useRef, useState } from "react";
//#region src/modules/Mail/screens/hooks/useMailMessageProvider.ts
var useMailMessageProvider = ({ mailMessageId }) => {
	const [mailAction, setMailAction] = useState();
	const [updatingFlagged, setUpdatingFlagged] = useState(false);
	const [updatingUserRead, setUpdatingUserRead] = useState(false);
	const [deletingMessage, setDeletingMessage] = useState(false);
	const [isAutoReadDisabled, setIsAutoReadDisabled] = useState(false);
	const actionRef = useRef(null);
	const { data, loading, refresh } = useMailMessage(mailMessageId ? { mailMessageId } : void 0);
	const { rpcService } = useRPCContext();
	const { data: threadData, isCalling: loadingThreadData } = useRPC(RPCs.Mail.GetMessagesList.v1, { threadId: data?.mailMessage?.threadId }, { fire: !!data });
	const { body, bodyHtml, receiverUserAndGroupIds, senderUserId } = data?.mailMessage || { receiverUserAndGroupIds: [] };
	const responseData = {
		body: "",
		bodyHtml: "",
		recipients: [],
		subjectPrefix: "RE"
	};
	if (mailAction === MailAction.ReplyAll) responseData.recipients = [...receiverUserAndGroupIds, senderUserId || ""];
	else if (mailAction === MailAction.Reply) responseData.recipients = [senderUserId || ""];
	else if (mailAction === MailAction.Forward) {
		responseData.body = body || "";
		responseData.bodyHtml = bodyHtml || "";
		responseData.subjectPrefix = "FW";
	}
	const readMessage = async () => {
		if (!mailMessageId) console.log("useMailMessageProvider.readMessage, no mailMessageId provided");
		else try {
			setUpdatingUserRead(true);
			await rpcService.Mail.SetMessageRead.v1.call({
				mailMessageIds: [mailMessageId],
				read: !data?.mailMessage?.userRead
			});
			setIsAutoReadDisabled(true);
			await refresh();
		} catch (err) {
			console.log("Mail.SetMessageRead.v1.call err", err);
		} finally {
			setUpdatingUserRead(false);
		}
	};
	const flagMessage = async () => {
		if (!mailMessageId) console.log("useMailMessageProvider.flagMessage, no mailMessageId provided");
		else try {
			setUpdatingFlagged(true);
			await rpcService.Mail.SetMessageFlagged.v1.call({
				flagged: !data?.mailMessage?.userFlagged,
				mailMessageIds: [mailMessageId]
			});
			await refresh();
		} catch (err) {
			console.log("Mail.SetMessageFlagged.v1.call err", err);
			Alerts.error("There was a problem flagging the message", err.message);
		} finally {
			setUpdatingFlagged(false);
		}
	};
	const deleteMessage = async () => {
		if (!mailMessageId) console.log("useMailMessageProvider.deleteMessage, no mailMessageId provided");
		else try {
			setDeletingMessage(true);
			await rpcService.Mail.DeleteMessages.v1.call({ mailMessageIds: [mailMessageId] });
			Alerts.success("Message deleted successfully");
		} catch (err) {
			console.log("Mail.DeleteMessage.v1.call err", err);
			Alerts.error("There was a problem deleting the message", err.message);
		} finally {
			setDeletingMessage(false);
		}
	};
	const linkedMessages = useMemo(() => {
		if (Array.isArray(threadData?.mailMessages) && threadData?.mailMessages.length) {
			const counts = threadData.mailMessages.reduce((acc, mailMessage) => {
				if (mailMessage.id === mailMessageId) acc.selectedMessageHasPassed = true;
				else acc.selectedMessageHasPassed ? acc.after = acc.after + 1 : acc.before = acc.before + 1;
				return acc;
			}, {
				after: 0,
				before: 0,
				selectedMessageHasPassed: false
			});
			return {
				after: counts.after,
				before: counts.before
			};
		}
		return {
			after: void 0,
			before: void 0
		};
	}, [threadData]);
	useEffect(() => {
		if (!isAutoReadDisabled && data?.mailMessage && !data.mailMessage.userRead) readMessage();
	}, [data?.mailMessage]);
	useEffect(() => {
		if (mailAction && actionRef.current) actionRef.current.scrollIntoView({ behavior: "smooth" });
	}, [mailAction]);
	return {
		deleteMessage,
		deletingMessage,
		flagMessage,
		linkedMessages,
		loadingMessage: loading,
		loadingThreadData,
		mailAction,
		mailMessage: data?.mailMessage,
		readMessage,
		responseData,
		setMailAction,
		threadData,
		updatingFlagged,
		updatingUserRead
	};
};
//#endregion
export { useMailMessageProvider };

//# sourceMappingURL=useMailMessageProvider.js.map